import { useCallback, useEffect, useRef, useState } from "react";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import { CareCaseCorrection } from "@/components/CareCaseCorrection";
import { CoachAssignmentPanel, useIsAdmin } from "@/components/CoachAssignment";
import { REVIEW_OUTCOME_LABEL } from "@/lib/lab-labels";
import {
  BLOOD_TEST_STATUS_LABELS,
  desiredOutcomeField,
  reproductiveStatusField,
  LAB_WEBDOC_ACTION_LABEL,
  PRESCRIPTION_CHOICE_LABEL,
  PRESCRIPTION_CHOICES,
  UNREADABLE_LAB_REPORT_LABEL,
  treatmentStatusOutcomesFor,
  type PrescriptionChoice,
} from "@care-by-ella/contracts";
import { LabOrderQuarantinedReports } from "@/components/LabOrderQuarantinedReports";
import { LabOrderResults } from "@/components/LabOrderResults";
import { issuePrescription } from "@/lib/work-queue-api";
import { offersSubscriptionLinkResend } from "@/lib/treatment-start";
import { IssuePrescriptionDialog } from "@/components/IssuePrescriptionDialog";
import { TreatmentStartPanel } from "@/components/TreatmentStartPanel";
import { OnboardingStatusCard } from "@/components/OnboardingStatusCard";

// TES-997 — the live states on which an issued prescription can be recorded
// later (PrescriptionCycleService.issuePrescription).
const ISSUABLE_CYCLE_STATES: readonly string[] = [
  "active",
  "expiring_soon",
  "renewal_pending",
  "doctor_review",
  "paused",
];
function isPrescriptionChoice(
  v: string | null | undefined,
): v is PrescriptionChoice {
  return (PRESCRIPTION_CHOICES as readonly string[]).includes(v ?? "");
}
import type {
  AiAssistOverviewFlag,
  ClinicianBloodTest,
  ClinicianMedicalEvidence,
  CreateSubscriptionLinkRequest,
  PatientAttentionTexts,
  PatientFormFacts,
  PublishTreatmentDecisionResult,
} from "@care-by-ella/contracts";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { DoctorDesiredSupportSection } from "@/components/DoctorDesiredSupportSection";
import { LabFeedbackPanel } from "@/components/LabFeedbackPanel";
import { MedicalEvidencePanel } from "@/components/MedicalEvidencePanel";
import { AiAssistOverviewPanel } from "@/components/meeting/AiAssistOverviewPanel";
import { UnderlagOchKompletteringar } from "@/components/UnderlagOchKompletteringar";
import { RiskFlagsPanel } from "@/components/RiskFlagsPanel";
import { AttentionTextsPanel } from "@/components/AttentionTextsPanel";
import { PatientChatPane } from "@/components/PatientChatPane";
import { PatientFlows } from "@/components/PatientFlows";
import { PatientJourneys } from "@/components/PatientJourneys";
import { PatientMeetings } from "@/components/PatientMeetings";
import { BookCareMeeting } from "@/components/BookCareMeeting";
import { MeetingWorkspace } from "@/components/meeting/MeetingWorkspace";
import { PatientOpenHandoffs } from "@/components/PatientOpenHandoffs";
import { GoalZoneNote } from "@/components/GoalZoneNote";
import { RenewalReviewPanel } from "@/components/RenewalReviewPanel";
import { PatientWellnessProgram } from "@/components/PatientWellnessProgram";
import { PatientNutritionCourses } from "@/components/PatientNutritionCourses";
import { PatientHandoff } from "@/components/PatientHandoff";
import { PatientHomeNow } from "@/components/PatientHomeNow";
import { PatientPhotos } from "@/components/PatientPhotos";
import { PatientHeader } from "@/components/PatientHeader";
import { SubscriptionPlanChoice } from "@/components/SubscriptionPlanChoice";
import { PatientEarlierTreatmentStart } from "@/components/PatientEarlierTreatmentStart";
import {
  labOrderToAnswer,
  TreatmentDecisionWorkspace,
} from "@/components/TreatmentDecisionWorkspace";
import { SummarySections } from "@/components/SummarySections";
import { CompletionNoteWorkspace } from "@/components/CompletionNoteWorkspace";
import { LabWebdocAssessmentDialog } from "@/components/LabWebdocAssessmentDialog";
import { labWebdocDoneNotice } from "@/lib/lab-webdoc-done";
import { completionNoteRoundId, currentRound } from "@/lib/completion-round";
import { WeeklyCheckinAnswers } from "@/components/WeeklyCheckinAnswers";
import { TestPatientPanel } from "@/components/TestPatientPanel";
import { TransferReviewPanel } from "@/components/TransferReviewPanel";
import { cycleStateLabel } from "@/lib/state-labels";
import { resendPlanPrefill } from "@/lib/transfer-plan";
import { ComorbidityAssessmentPanel } from "@/components/ComorbidityAssessmentPanel";
import { LowBmiAssessmentPanel } from "@/components/LowBmiAssessmentPanel";
import { PatientMeasurements } from "@/components/PatientMeasurements";
import { PatientMedicalProfile } from "@/components/PatientMedicalProfile";
import { MedicalReviewCases } from "@/components/MedicalReviewCases";
import { SendDoctorNotice } from "@/components/SendDoctorNotice";
import { DoctorQuestions } from "@/components/DoctorQuestions";
import { DoseLogPanel } from "@/components/DoseLogPanel";
import { SymptomEpisodesPanel } from "@/components/SymptomEpisodesPanel";
import { WellbeingSeriesPanel } from "@/components/WellbeingSeriesPanel";
import { DoseSchedulePanel } from "@/components/DoseSchedulePanel";
import { TransferTreatmentStartPanel } from "@/components/TransferTreatmentStartPanel";
import { OverviewCompletionRequest } from "@/components/OverviewCompletionRequest";
import { CareMeetingRequirementPanel } from "@/components/CareMeetingRequirementPanel";
import { AssessmentPhotosPanel } from "@/components/AssessmentPhotosPanel";
import { MedicalCaseOutcomePanel } from "@/components/MedicalCaseOutcomePanel";
import { F01SafetyPanel } from "@/components/F01SafetyPanel";
import {
  fetchPatient,
  fetchPatientFormFacts,
  acknowledgeRiskFlag,
  clearRiskFlag,
  markSubmissionReviewed,
  sendSubscriptionLink,
  startDoctorQuestionMedicalAction,
  UnauthorizedError,
  type PatientDetail,
} from "@/lib/clinician-api";
import {
  DECISION_STEP,
  isMedicalActionAnchor,
  isPatientTab,
  MEDICAL_ACTION_ANCHOR,
  openMeetingId,
  STEP_PARAM,
  patientHref,
  patientMeetingHref,
  patientTreatmentStartHref,
  pendingAnswerActionFrom,
  prescriptionReviewLanding,
  transferLandingTab,
  withMeeting,
  withTab,
  type MedicalActionKey,
  type PatientTab,
} from "@/lib/patient-links";
import { useReadOnly, useViewPath } from "@/components/SupportView";
import { LabOrderRequisitionRef } from "@/components/LabOrderRequisitionRef";
import { errorText } from "@/lib/error-text";

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
const APPROVE_SHORTCUT_HINT = IS_MAC ? "⌘↵" : "Ctrl↵";

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

// TES-524 — F-01 Q12 option labels straight from the contract, so the doctor
// reads the same words the patient chose (the values are the stable ids).
function reproductiveStatusLabel(value: string): string {
  const opt = (reproductiveStatusField().options ?? []).find(
    (o) => o.value === value,
  );
  return opt?.label.sv ?? value;
}

function desiredOutcomeLabel(value: string): string {
  const opt = (desiredOutcomeField().options ?? []).find(
    (o) => o.value === value,
  );
  return opt?.label.sv ?? value;
}

// TES-942 — the order's own status in Swedish. `resulted` is a doctor's
// manual marking (there is no Webdoc integration), so it says so rather than
// "svar mottagna".
const LAB_ORDER_STATUS_LABEL: Record<string, string> = {
  ordered: "Beställt",
  remiss_sent: "Remiss skickad",
  // TES-1337 — a partial is a lab delivery (LabPortalen), never a manual
  // marking. Proposed copy.
  partial: "Delsvar inkommet",
  resulted: "Provsvar markerat som inkommet",
  cancelled: "Avbrutet",
};

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// TES-1037 — "Tillbaka" returns to wherever the doctor came from (a work
// view, the meeting list, a queue). The health-declaration list it used to
// point at is no longer in the menu; opened directly, it goes to the role's
// home page instead.
function BackLink({ className }: { className: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const viewPath = useViewPath();
  const hasInAppHistory = location.key !== "default";
  return (
    <Link
      to={viewPath("/")}
      className={className}
      onClick={(e) => {
        if (!hasInAppHistory) return;
        e.preventDefault();
        navigate(-1);
      }}
    >
      ← Tillbaka
    </Link>
  );
}

export function PatientDetailRoute() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { signOut, claims } = useAuth();
  // TES-1218 — the coach section is the admin's; a doctor has no route to it.
  const isAdmin = useIsAdmin();
  // TES-1040 — the admin's read-only support view: every control that
  // changes the patient's state is hidden, and so are the chat and photos.
  const readOnly = useReadOnly();
  const viewPath = useViewPath();
  const [data, setData] = useState<PatientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TES-952 — the one decision workspace is open.
  const [decisionOpen, setDecisionOpen] = useState(false);
  // TES-1149 — bumped when a notice is sent, so the questions list reloads.
  const [noticesSent, setNoticesSent] = useState(0);
  // TES-506 — the tab lives in the URL, not in component state.
  //
  // The 2026-09-04 walkthrough: clicking a chat in the list must take you to
  // the right tab. The chat list already linked to `/patients/:id#chat` — it always
  // had. The link could never work: the tab was `useState("oversikt")` and
  // nothing on this page ever read the location, so every deep link landed on
  // "Översikt" regardless of what it asked for.
  //
  // `?flik=` is canonical (it survives a reload and can be shared); the hash
  // is still honoured for links written before this change. An UNKNOWN hash is
  // deliberately ignored rather than defaulted-away, because `#cycles` is a
  // real anchor into a section of "Översikt" — the side-effects lane links to it
  // — and treating it as a bad tab would break the scroll target.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const requested =
    searchParams.get("flik") ?? location.hash.replace(/^#/, "") ?? "";
  const tab: PatientTab =
    isPatientTab(requested) && !(readOnly && requested === "chat")
      ? requested
      : "oversikt";
  // TES-955 — the open meeting (`?mote=`) is kept across tabs, so the
  // workspace (and a call in it) survives a look at the overview.
  const setTab = useCallback(
    (next: PatientTab) => {
      setSearchParams((prev) => withTab(prev, next), { replace: true });
    },
    [setSearchParams],
  );
  // TES-955 (TES-941 "§VÅRDMÖTESVYN") — the care meeting is a tab under the
  // patient. Opening or closing one is a navigation, so Back returns to the
  // list.
  const meetingId = openMeetingId(searchParams);
  const setMeeting = useCallback(
    (next: string | null) => {
      setSearchParams((prev) => withMeeting(prev, next));
    },
    [setSearchParams],
  );
  const [inCall, setInCall] = useState(false);
  // The pinned header's height, for the meeting's sticky video column: it
  // sits right under the header whatever the header holds this time.
  const stickyRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeight, setStickyHeight] = useState(0);
  // CAR-115 — doctor-sent subscription link. TES-952: sent by the positive
  // decision itself, or re-sent afterwards from the decision card.
  const [subLink, setSubLink] = useState<{
    url: string;
    emailed: boolean;
  } | null>(null);
  const [subLinkBusy, setSubLinkBusy] = useState(false);
  // TES-942 — the current case's derived blood-test status, from the same
  // response as the medical-evidence panel. `undefined` until it has loaded.
  const [bloodTest, setBloodTest] = useState<
    ClinicianBloodTest | null | undefined
  >(undefined);
  const [evidenceKey, setEvidenceKey] = useState(0);
  // TES-1200 — bumped when the doctor closes or reopens the medical case, so
  // the komplettering button re-reads its verdict.
  const [medicalCaseVersion, setMedicalCaseVersion] = useState(0);
  // TES-1400 (Ö-576) — the answer to the doctor's question that "Starta
  // medicinsk åtgärd" came from, and the action chosen. Kept in state, not
  // read from the URL each time: "Öppna receptbedömning" replaces the
  // query when it picks the review. The answer is marked handled when that
  // action is created on this page (`actionCreated`), and only then.
  const [pendingAnswer, setPendingAnswer] = useState(() =>
    pendingAnswerActionFrom(location.search),
  );
  const [answerHandled, setAnswerHandled] = useState(false);
  useEffect(() => {
    const next = pendingAnswerActionFrom(location.search);
    if (next) {
      setPendingAnswer(next);
      setAnswerHandled(false);
    }
  }, [location.search]);
  useEffect(() => {
    // Another patient: an answer from this one is not hers.
    if (!pendingAnswerActionFrom(window.location.search)) {
      setPendingAnswer(null);
      setAnswerHandled(false);
    }
  }, [id]);
  const actionCreated = useCallback(
    async (action: MedicalActionKey) => {
      if (!pendingAnswer || pendingAnswer.action !== action || !id) return;
      try {
        await startDoctorQuestionMedicalAction(
          id,
          pendingAnswer.taskId,
          action,
        );
        setPendingAnswer(null);
        setAnswerHandled(true);
      } catch (e) {
        if (e instanceof UnauthorizedError) void signOut();
        else
          setError(
            "Åtgärden är skapad, men svaret kunde inte markeras som hanterat. Markera det i arbetskön.",
          );
      }
    },
    [pendingAnswer, id, signOut],
  );
  // TES-1060 — the completion rounds from the same response: the newest
  // one with something supplied gets its Kompletteringsanteckning, and the
  // current case's round is where a lab assessment is saved.
  const [evidence, setEvidence] = useState<ClinicianMedicalEvidence | null>(
    null,
  );
  const [labWebdocOpen, setLabWebdocOpen] = useState(false);
  const [labWebdocNotice, setLabWebdocNotice] = useState<string | null>(null);
  // TES-519 — the two CTAs behind the Incheckningar and Medicinska larm lanes.
  const [reviewBusyId, setReviewBusyId] = useState<string | null>(null);
  const [flagBusyId, setFlagBusyId] = useState<string | null>(null);
  // TES-948 — the form-facts response the overview panel fetched, for the
  // "Riskflaggor: X" list (one logged read, not two).
  // TES-1001 — the free texts ride on the same read.
  const [formFacts, setFormFacts] = useState<{
    flags: AiAssistOverviewFlag[] | null;
    attention: PatientAttentionTexts | null;
    error: string | null;
  }>({ flags: null, attention: null, error: null });
  const onFormFacts = useCallback(
    (facts: PatientFormFacts | null, error: string | null) =>
      setFormFacts({
        flags: facts?.flags ?? null,
        // An older server without the field: an empty list, not "loading".
        attention: facts
          ? (facts.attention ?? { items: [], sources: [] })
          : null,
        error,
      }),
    [],
  );
  // Another patient's flags must never stand under this one's name while
  // her own are loading.
  useEffect(() => {
    setFormFacts({ flags: null, attention: null, error: null });
  }, [id]);

  // TES-1034 (Emelie, point 4) — the transfer view shows the free-text list
  // too, from the same form-facts read (texts that belong to a flag are
  // already left out by the server). On the overview tab the overview panel
  // fetches it; here it is fetched once if nothing has yet.
  const needsFactsForTransfer =
    tab === "overtag" && formFacts.attention === null && !formFacts.error;
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  useEffect(() => {
    if (!needsFactsForTransfer || !id) return;
    let live = true;
    fetchPatientFormFacts(id)
      .then((d) => {
        if (live) onFormFacts(d, null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) void signOutRef.current();
        else onFormFacts(null, "Kunde inte hämta patientens fritextsvar.");
      });
    return () => {
      live = false;
    };
  }, [needsFactsForTransfer, id, onFormFacts]);

  // TES-997 (Ö-259) — "Nytt recept är utfärdat i Webdoc", recorded on the
  // live cycle when the decision published without an issued prescription.
  // TES-1055 — through the Webdoc step (IssuePrescriptionDialog), not a
  // browser confirm: the API refuses it without the journal tick.
  const [issueBusyId, setIssueBusyId] = useState<string | null>(null);
  const [issueTarget, setIssueTarget] = useState<{
    cycleId: string;
    cycleNumber: number;
  } | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);
  async function recordIssuedPrescription(cycleId: string) {
    setIssueBusyId(cycleId);
    setIssueError(null);
    try {
      await issuePrescription(id, cycleId, {
        webdocControl: { journalInWebdoc: true },
      });
      setIssueTarget(null);
      load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      // A refusal's code in words. TES-1531 (Ö-791) — a missing F-05 no
      // longer refuses this registration; it stays shown on the review.
      setIssueError(errorText(e instanceof Error ? e.message : String(e)));
    } finally {
      setIssueBusyId(null);
    }
  }

  async function reviewSubmission(submissionId: string) {
    setReviewBusyId(submissionId);
    setError(null);
    try {
      await markSubmissionReviewed(id, submissionId);
      load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setReviewBusyId(null);
    }
  }

  // TES-676 — two steps, two facts. "Mottaget" says the doctor has SEEN the
  // signal; "Avsluta" says it is handled. The chain has to be able to show
  // the time between them, so the buttons are not one.
  async function acknowledgeFlag(flagId: string) {
    setFlagBusyId(flagId);
    setError(null);
    try {
      await acknowledgeRiskFlag(id, flagId);
      load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFlagBusyId(null);
    }
  }

  async function clearFlag(flagId: string) {
    setFlagBusyId(flagId);
    setError(null);
    try {
      await clearRiskFlag(id, flagId);
      load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFlagBusyId(null);
    }
  }

  const load = useCallback(() => {
    fetchPatient(id)
      .then(setData)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [id, signOut]);

  useEffect(() => {
    load();
  }, [load]);

  const hasData = data !== null;
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setStickyHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasData]);

  // TES-942 — a lab action changes the derived blood-test status too, so the
  // medical-evidence panel re-reads with the patient.
  function reloadLabs() {
    setEvidenceKey((k) => k + 1);
    load();
  }

  // TES-458 — a transfer patient opens on her case: the review is what the
  // doctor came for. Keyed on the review id so a reload after a decision
  // (same review) does not yank the doctor off whatever tab she moved to.
  //
  // TES-506: now that the tab is in the URL, this only fires when the doctor
  // arrived without asking for a tab — an explicit `?flik=blodprov` on a
  // transfer patient is her choice and must not be overridden.
  //
  // TES-1034 — and only on the first arrival for this review: choosing
  // "Översikt" drops `?flik`, and re-deciding on every render bounced the
  // doctor straight back to "overtag". See `transferLandingTab`.
  const transferReviewId = data?.transferReview?.id ?? null;
  const askedForTab = isPatientTab(requested);
  const landedForReview = useRef<string | null>(null);
  useEffect(() => {
    landedForReview.current = null;
  }, [id]);
  useEffect(() => {
    const next = transferLandingTab({
      reviewId: transferReviewId,
      askedForTab,
      handled: landedForReview.current,
    });
    landedForReview.current = next.handled;
    if (next.tab) setTab(next.tab);
  }, [transferReviewId, askedForTab, setTab]);

  // TES-1335 — the work queue's "Starta medicinsk åtgärd" lands here on an
  // anchor naming the function (medicalActionHref). Once the patient has
  // loaded, "Öppna receptbedömning" picks the review she has open; every
  // other action scrolls to its section. Once per patient and anchor, so a
  // reload of the data never yanks the page back.
  const landedAction = useRef<string | null>(null);
  const hasTransferReview = !!data?.transferReview;
  const hasRenewalReview = !!data?.renewalReview;
  const dataLoaded = !!data;
  useEffect(() => {
    const anchor = location.hash.replace(/^#/, "");
    if (!dataLoaded || !id || !isMedicalActionAnchor(anchor)) return;
    const key = `${id}#${anchor}`;
    if (landedAction.current === key) return;
    landedAction.current = key;
    if (anchor === MEDICAL_ACTION_ANCHOR.prescription_review) {
      const landing = prescriptionReviewLanding({
        hasTransferReview,
        hasRenewalReview,
      });
      // The overview's "Bekräfta receptstatus" scrolls itself into view
      // (TreatmentStartPanel); the path is kept, so a view prefix stays.
      if (landing.anchor) {
        navigate(
          { search: `?flik=${landing.tab}`, hash: `#${landing.anchor}` },
          { replace: true },
        );
      } else setTab(landing.tab);
      return;
    }
    requestAnimationFrame(() =>
      document
        .getElementById(anchor)
        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  }, [
    dataLoaded,
    id,
    location.hash,
    hasTransferReview,
    hasRenewalReview,
    navigate,
    setTab,
  ]);

  // TES-448 — a transfer patient is decided through her review, never by the
  // onboarding approve (the API 409s it; the shortcut must not offer it).
  // TES-952 — the same condition opens the one decision workspace.
  // TES-1060 — the round whose Kompletteringsanteckning is shown: the
  // newest one where something has come in.
  const noteRoundId = completionNoteRoundId(evidence);

  // TES-1071 — since TES-1069 a positive decision leaves the treatment in
  // `onboarding` until the prescription is confirmed after payment, so the
  // shared treatment-start state says whether one is already out.
  const decisionOut =
    data?.treatmentStartState !== undefined &&
    data.treatmentStartState !== "NO_POSITIVE_DECISION";
  const canDecide =
    !readOnly &&
    data?.treatment?.state === "onboarding" &&
    data?.patientGroup !== "PRESCRIPTION_TRANSFER" &&
    !decisionOut &&
    !!data?.formSubmissions.find((s) => s.templateKey === "intake");
  // TES-1291 — the same workspace for a running treatment: pause,
  // resumption and end.
  const canDecideStatus =
    !readOnly && treatmentStatusOutcomesFor(data?.treatment?.state).length > 0;
  const canOpenDecision = canDecide || canDecideStatus;

  // The link can only be re-sent once a positive decision is published — the
  // server's TES-902 gate refuses it before, so the control does not offer it.
  const positiveDecisionPublished =
    data?.transferReview?.status === "approved" ||
    decisionOut ||
    !!data?.treatment?.cycles.some(
      (c) => c.state !== "pending_assessment" && c.state !== "rejected",
    );

  useEffect(() => {
    // Another patient's decision must never be open under this one's name.
    setDecisionOpen(false);
    setSubLink(null);
  }, [id]);

  // TES-1245 (A-02) — the onboarding work item opens the decision workspace
  // directly (`?steg=beslut`). One-shot: consumed once THIS patient's data
  // has loaded, opened only if the decision is still open (canDecide), and
  // dropped from the URL either way so a reload or Back does not reopen it.
  const stepRequested = searchParams.get(STEP_PARAM) === DECISION_STEP;
  const loadedPatientId = data?.patient.id ?? null;
  useEffect(() => {
    if (!stepRequested || loadedPatientId !== id) return;
    if (canDecide) setDecisionOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(STEP_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [stepRequested, loadedPatientId, id, canDecide, setSearchParams]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (isTypingTarget(document.activeElement)) return;
        if (!canDecide || decisionOpen) return;
        e.preventDefault();
        setDecisionOpen(true);
        return;
      }
      if (e.key === "Escape" && decisionOpen) {
        e.preventDefault();
        setDecisionOpen(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canDecide, decisionOpen]);

  // TES-1291 — a pause, resumption or end is published: the page reloads to
  // show the treatment's new state.
  function onStatusDecisionPublished() {
    setDecisionOpen(false);
    load();
    void actionCreated("treatment_plan");
  }

  // TES-952 — the decision is published. She stays on the page: on approval
  // the subscription link it sent is shown here, to share in the call.
  function onDecisionPublished(result: PublishTreatmentDecisionResult) {
    setDecisionOpen(false);
    if (result.subscriptionLink) {
      setSubLink({
        url: result.subscriptionLink.url,
        emailed: result.subscriptionLink.emailed,
      });
    }
    reloadLabs();
    void actionCreated("treatment_plan");
  }

  // TES-907 (Ö-221) — the plan comes from the doctor's explicit choice; there
  // is no default to fall back on here, by design.
  async function resendLink(choice: CreateSubscriptionLinkRequest) {
    setSubLinkBusy(true);
    setError(null);
    try {
      const { url, emailed } = await sendSubscriptionLink(id, choice);
      setSubLink({ url, emailed });
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubLinkBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="flex items-center justify-between">
        <BackLink className="text-sm text-brand hover:underline" />
        {error ? (
          <span className="text-danger text-sm">{error}</span>
        ) : (
          <span className="text-textSecondary text-sm">Hämtar…</span>
        )}
      </div>
    );
  }

  const intake = data.formSubmissions.find((s) => s.templateKey === "intake");
  // CAR-92: weekly check-ins, most recent first (the API already sorts
  // formSubmissions by submittedAt desc).
  const weeklyCheckins = data.formSubmissions.filter(
    (s) => s.templateKey === "weekly_checkin",
  );
  // TES-520 — the operative rail: what is booked, soonest first (max three).
  const upcomingMeetings = data.meetings
    .filter(
      (m) =>
        (m.status === "scheduled" || m.status === "in_progress") &&
        new Date(m.scheduledAt).getTime() > Date.now() - 60 * 60 * 1000,
    )
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
    .slice(0, 3);

  return (
    <div
      className="flex flex-col gap-lg"
      style={
        { "--patient-sticky-h": `${stickyHeight}px` } as React.CSSProperties
      }
    >
      {/* TES-520 — who + the numbers, pinned above the tabs on every tab. */}
      <div
        ref={stickyRef}
        className="sticky top-0 z-20 -mt-md flex flex-col gap-md bg-background pt-md pb-sm lg:-mt-lg lg:pt-lg"
      >
        <BackLink className="w-fit text-sm text-brand hover:underline" />
        <PatientHeader
          data={data}
          intake={intake}
          lastCheckinAt={weeklyCheckins[0]?.submittedAt ?? null}
          // TES-955 — the name goes to the overview; an open meeting stays
          // open, so the way back to it is one click on "Vårdmöten".
          nameHref={viewPath(patientHref(id, "oversikt", meetingId))}
          // TES-863 (Ö-229) — the patient page is the one surface where a
          // correction belongs, so it is the one that passes a handler.
          onGoalWeightAdjusted={readOnly ? undefined : load}
          actions={
            <>
              {/* TES-952 — one entry, not "Godkänn"/"Avslå" plus a free-standing
                  subscription link: the same workspace the Blodprov tab
                  opens, reachable here for a decision straight after the
                  meeting when the evidence is already complete. */}
              {canDecide ? (
                <button
                  type="button"
                  onClick={() => setDecisionOpen(true)}
                  className="px-md py-sm rounded-md bg-brand text-textOnBrand text-sm hover:opacity-90"
                  title={`Lämna svar till patienten och fatta behandlingsbeslut (${APPROVE_SHORTCUT_HINT})`}
                >
                  Lämna svar och fatta beslut{" "}
                  <span className="opacity-60 ml-xs text-xs">
                    {APPROVE_SHORTCUT_HINT}
                  </span>
                </button>
              ) : null}
              {/* TES-1291 — the same workspace for a running treatment. */}
              {canDecideStatus ? (
                <button
                  type="button"
                  onClick={() => setDecisionOpen(true)}
                  className="px-md py-sm rounded-md border border-brand text-brand text-sm hover:bg-brand/5"
                >
                  Behandlingsbeslut
                </button>
              ) : null}
            </>
          }
        />

        {error ? (
          <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
            {error}
          </div>
        ) : null}

        {/* TES-1400 (Ö-576) — where "Starta medicinsk åtgärd" left the
            answer. Proposed copy. */}
        {pendingAnswer && !readOnly ? (
          <div
            role="status"
            className="rounded-md border border-brand/30 bg-brand/5 p-md text-sm text-textPrimary"
          >
            Svaret på läkarens fråga markeras som hanterat när åtgärden är
            skapad. Avbryter du ligger svaret kvar i arbetskön.
          </div>
        ) : answerHandled ? (
          <div
            role="status"
            className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-md text-sm text-textSecondary"
          >
            Åtgärden är skapad och svaret på läkarens fråga är markerat som
            hanterat.
          </div>
        ) : null}

        {/* TES-966 — on a phone the tabs scroll sideways instead of
            squeezing; from `lg` up they lay out exactly as before. */}
        <nav className="flex gap-lg overflow-x-auto overflow-y-hidden border-b border-surfaceMuted lg:overflow-visible">
          {(
            [
              ["oversikt", "Översikt"],
              // TES-458 — the case view, only where there is a case.
              ...(data.transferReview
                ? ([["overtag", "Receptövertag"]] as const)
                : []),
              ["moten", "Vårdmöten"],
              // TES-519 — only while a cycle waits on the doctor.
              ...(data.renewalReview
                ? ([["manad", "Månadsincheckning"]] as const)
                : []),
              ["blodprov", "Blodprov"],
              ["veckor", "Veckoincheckningar"],
              // TES-1040 — no chat in the admin's read-only view.
              ...(readOnly ? [] : ([["chat", "Meddelanden"]] as const)),
              // TES-298 — only test patients get the simulate & observe tab.
              ...(data.patient.isTest ? ([["test", "Test"]] as const) : []),
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`shrink-0 whitespace-nowrap pb-sm -mb-px border-b-2 text-sm transition lg:shrink lg:whitespace-normal ${
                tab === key
                  ? "border-brand text-textPrimary font-medium"
                  : "border-transparent text-textSecondary hover:text-textPrimary"
              }`}
            >
              {label}
              {key === "moten" && inCall ? (
                <span
                  className="ml-xs inline-block h-[8px] w-[8px] rounded-full bg-notify align-middle"
                  title="Pågående samtal"
                  aria-label="pågående samtal"
                />
              ) : null}
            </button>
          ))}
        </nav>
      </div>

      {/* TES-952 — the link an approval (or a resend) just sent. Above the
          tabs, because the decision is published from any tab and Kirill may
          need to share the link in the call when she has no verified email. */}
      {subLink ? (
        <p
          role="status"
          className="rounded-md border border-surfaceMuted bg-surface p-sm text-xs text-textSecondary"
        >
          {/* TES-1071 — a positive decision now requires a verified
              address, so "not e-mailed" is mostly a test patient (never
              mailed, TES-298) or a failed send; saying she has no verified
              address contradicted the header's "E-post bekräftad". */}
          {subLink.emailed
            ? "Abonnemangslänken är skickad via e-post. "
            : data.patient.isTest
              ? "Testpatient — ingen e-post skickas. Dela länken: "
              : data.patient.emailStatus === "verified"
                ? "E-postutskicket misslyckades — dela länken: "
                : "Patienten saknar verifierad e-post — dela länken: "}
          <a
            href={subLink.url}
            className="font-mono break-all underline text-brand"
          >
            {subLink.url}
          </a>
          {/* TES-1150 — the same session goes on to the prescription status,
              before the payment. Only while that step is still open. */}
          {id && data.onboardingStatus?.waitingOn === "doctor" ? (
            <>
              {" "}
              <Link
                to={patientTreatmentStartHref(id)}
                className="underline text-brand"
              >
                Nästa steg: bekräfta receptstatus
              </Link>
            </>
          ) : null}
        </p>
      ) : null}

      {/* TES-1073 (Ö-298) — the documented earlier treatment start, where
          the plan is chosen: above the transfer review and above the
          treatment decision. "Ella Underhåll" counts from it. */}
      {(tab === "overtag" || tab === "blodprov") && id ? (
        <PatientEarlierTreatmentStart
          key={`earlier-treatment-start:${id}`}
          patientId={id}
        />
      ) : null}
      {/* TES-457/458 — the case view: F-07 and the bound F-02 side by side,
          her attachments, the reading, and the decision. */}
      {tab === "overtag" && id && data.transferReview ? (
        <TransferReviewPanel
          key={data.transferReview.id}
          patientId={id}
          review={data.transferReview}
          awaitingSubscription={
            data.clinicalStage === "approved_awaiting_subscription"
          }
          transferSubmission={
            data.formSubmissions.find(
              (s) => s.id === data.transferReview?.transferSubmissionId,
            ) ?? null
          }
          intakeSubmission={
            data.formSubmissions.find(
              (s) => s.id === data.transferReview?.intakeSubmissionId,
            ) ?? null
          }
          onUnauthorized={() => void signOut()}
          onDecided={() => {
            load();
            void actionCreated("prescription_review");
          }}
          onStale={load}
        />
      ) : null}
      {tab === "overtag" && data.transferReview ? (
        <AttentionTextsPanel
          attention={formFacts.attention}
          error={formFacts.error}
          patientGroup={data.patientGroup}
        />
      ) : null}

      {/* TES-1117 — the prominent onboarding card: journey, current step,
          next step, who we wait on and the latest activity, opening (S2)
          the dated timeline. Renders nothing once onboarding is over. */}
      {tab === "oversikt" ? (
        <OnboardingStatusCard
          status={data.onboardingStatus}
          timeline={data.onboardingTimeline}
        />
      ) : null}

      {/* TES-1070 — "Bekräfta recept och publicera behandlingen", on top of
          the treatment area. Live after a verified subscription; TES-1141:
          shown locked between the positive decision and the subscription.
          Keyed on the clinical stage so a decision or payment that moves
          her on reloads it. */}
      {(tab === "oversikt" || tab === "overtag") && id ? (
        <TreatmentStartPanel
          key={`treatment-start:${id}:${data.clinicalStage ?? ""}`}
          patientId={id}
          onUnauthorized={() => void signOut()}
          onConfirmed={() => {
            load();
            void actionCreated("prescription_review");
          }}
        />
      ) : null}

      {/* TES-1169 — "Begär komplettering": the meeting's CP-06 komplettering,
          available whenever there is an active medical case. Doctor only
          (the component renders nothing for anyone else). Keyed on the
          clinical stage so a decision that moves her on re-reads it. */}
      {/* TES-1200 (Ö-403) — the medical case: active / Pausat and the
          history. TES-1555 — its one documented ending is an ended care
          relationship. The doctor documents and revokes it; the admin's
          support view reads it. */}
      {tab === "oversikt" && id && (claims?.role === "doctor" || readOnly) ? (
        <section
          id={MEDICAL_ACTION_ANCHOR.register_medical_case}
          className="bg-surface border border-surfaceMuted rounded-md p-md"
        >
          <h2 className="font-display text-lg mb-xs">Medicinskt ärende</h2>
          <MedicalCaseOutcomePanel
            key={`medical-case:${id}:${data.clinicalStage ?? ""}`}
            patientId={id}
            canRecord={claims?.role === "doctor" && !readOnly}
            onChanged={() => {
              setMedicalCaseVersion((v) => v + 1);
              void actionCreated("register_medical_case");
            }}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-1218 (Ö-407) — her primary coach and the change log, with
          "Tilldela coach". Admin only, also in the read-only support view:
          assigning a coach is the admin's own action. */}
      {tab === "oversikt" && id && isAdmin ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-xs">Primär coach</h2>
          <CoachAssignmentPanel
            patientId={id}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-1602 — "Kräv vårdmöte" on the optional care meeting track.
          Renders nothing unless the server says it applies (flag off: no
          change). */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <CareMeetingRequirementPanel
          key={`care-meeting-requirement:${id}:${data.clinicalStage ?? ""}`}
          patientId={id}
          onUnauthorized={() => void signOut()}
        />
      ) : null}

      {/* TES-1648 — the written track's three photos and their review.
          Renders nothing unless the task applies to her. */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <AssessmentPhotosPanel
          key={`assessment-photos:${id}:${data.clinicalStage ?? ""}`}
          patientId={id}
          onUnauthorized={() => void signOut()}
        />
      ) : null}

      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <section
          id={MEDICAL_ACTION_ANCHOR.request_completion}
          className="bg-surface border border-surfaceMuted rounded-md p-md"
        >
          <h2 className="font-display text-lg mb-xs">Komplettering</h2>
          <OverviewCompletionRequest
            key={`overview-completion:${id}:${data.clinicalStage ?? ""}:${medicalCaseVersion}`}
            patientId={id}
            onUnauthorized={() => void signOut()}
            onSent={() => {
              setEvidenceKey((k) => k + 1);
              void actionCreated("request_completion");
            }}
          />
        </section>
      ) : null}

      {/* TES-1194 — the weekly dose schedule: "Ändra ordinarie dosdag" and,
          after a pause, "Ny startdag efter uppehåll". Doctor only (the
          component renders nothing for anyone else; the coach may not move
          the dose day). Keyed on the clinical stage like the panels above. */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-xs">Dosschema</h2>
          <DoseSchedulePanel
            key={`dose-schedule:${id}:${data.clinicalStage ?? ""}`}
            patientId={id}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-1398 — an approved transfer's treatment start: exact or
          estimated, the treatment day and DT it gives her, and the doctor's
          correction. Doctor only; renders nothing for any other patient. */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <TransferTreatmentStartPanel
          key={`transfer-start:${id}:${data.clinicalStage ?? ""}`}
          patientId={id}
          onUnauthorized={() => void signOut()}
        />
      ) : null}

      {/* TES-1151 S7 (Ö-423) — her dose log, read only. Doctor only: the
          coach has no direct access, and the admin's read-only view does not
          include it. */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-xs">Dosloggen</h2>
          <DoseLogPanel
            key={`dose-log:${id}`}
            patientId={id}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-1252 — her symptom episodes and older-format registrations,
          read only. Doctor only, like the dose log: symptom details are never
          coach data (A-01), and the admin's read-only view does not include
          them. */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-xs">Besvär</h2>
          <SymptomEpisodesPanel
            key={`symptoms:${id}`}
            patientId={id}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-1446 (Ö-654, Ö-675) — her "Mående", the series her own "Din
          hälsa över tid" shows, F-05's week as one point. Doctor only, like
          "Besvär": wellbeing answers are medical data the coach never sees
          (A-01), and the admin's read-only view does not include them. */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-xs">Mående</h2>
          <WellbeingSeriesPanel
            key={`wellbeing:${id}`}
            patientId={id}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-1147 — the doctor reaches her with a notice and, when she has
          to do something, a task; never by opening a chat. Doctor only (the
          component renders nothing for anyone else). */}
      {tab === "oversikt" && id && claims?.role === "doctor" && !readOnly ? (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-xs">Besked till patienten</h2>
          <SendDoctorNotice
            patientId={id}
            onUnauthorized={() => void signOut()}
            onSent={() => setNoticesSent((n) => n + 1)}
          />
          {/* TES-1149 — his questions, their status and her answers. */}
          <DoctorQuestions
            patientId={id}
            onUnauthorized={() => void signOut()}
            reloadKey={noticesSent}
          />
        </section>
      ) : null}

      {/* TES-520 — "Översikt" in two columns: the clinical on the left, the
          operational on the right. Nothing empty gets a card — a section
          without content is not rendered at all; the header above already says
          where the patient stands. */}
      {tab === "oversikt" ? (
        <div className="grid grid-cols-1 gap-lg lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="flex min-w-0 flex-col gap-lg">
            {/* TES-948 / TES-949 — "Riskflaggor: X" and the evidence card
                ("Underlag och kompletteringar" since TES-1329) side by side
                at the top. The list is the
                ONE place flags show on this page. TES-1001 (Ö-250): the open
                alarms (TES-519/676) are their own "Akutlarm: X – agera nu"
                block on top, never also counted as risk flags. The
                "Medicinska larm" lane lands here (#larm); "Mottaget" and
                "Avsluta larmet" stay on the alarm's row. */}
            <div className="grid grid-cols-1 gap-lg xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <RiskFlagsPanel
                id="larm"
                flags={formFacts.flags}
                flagsError={formFacts.error}
                sources={formFacts.attention?.sources}
                alarms={data.riskFlags ?? []}
                patientGroup={data.patientGroup}
                busyAlarmId={flagBusyId}
                onAcknowledge={
                  readOnly
                    ? undefined
                    : (alarmId) => void acknowledgeFlag(alarmId)
                }
                onClear={
                  readOnly ? undefined : (alarmId) => void clearFlag(alarmId)
                }
              />
              {/* TES-1329 — "Underlag och kompletteringar": what she
                  handed over before the assessment, by her journey, and
                  apart from it what the doctor has asked for since. */}
              {id ? (
                <UnderlagOchKompletteringar
                  patientId={id}
                  patientGroup={data.patientGroup}
                  transferReview={data.transferReview}
                  reloadKey={evidenceKey}
                  onUnauthorized={() => void signOut()}
                />
              ) : null}
            </div>

            {/* TES-1164 (Ö-383) — type 1 diabetes and MTC/MEN 2, from F-01
                (or, for a patient F-01 never asked, from F-02), next to the
                flags they raise. */}
            <F01SafetyPanel rows={data.f01SafetyAnswers} />

            {/* TES-1001 (Ö-249) — every free text she wrote, under the risk
                flags and apart from them: visibility, not severity. */}
            <AttentionTextsPanel
              attention={formFacts.attention}
              error={formFacts.error}
              patientGroup={data.patientGroup}
            />

            {/* TES-943 — the forms at a glance, the same panel as in the
                meeting: code-computed facts whether or not the AI switch is
                on (TES-941), the model's prose only when it is. Its flags are
                in the list above (TES-948), so it hands them up. */}
            {id ? (
              <AiAssistOverviewPanel
                patientId={id}
                patientGroup={data.patientGroup}
                onUnauthorized={() => void signOut()}
                hideFlags
                onFacts={onFormFacts}
              />
            ) : null}

            {/* TES-1322 — her desired support, her own stated wish: its own
                neutral section, outside the flags and the AI panel. */}
            <DoctorDesiredSupportSection f01={data.f01} />

            {intake ? (
              <>
                {/* TES-524 (Ö-79) — the goal F-02 reuses from F-01, with its
              provenance: source, instrument version, timestamp. Rendered from
              the F-01 answer itself, never from a copy in the intake. */}
                {data.f01 && data.f01.desiredOutcome.length > 0 ? (
                  <section className="bg-surface border border-surfaceMuted rounded-md p-md">
                    <h2 className="font-display text-lg mb-xs">
                      Viktigast för patienten just nu
                    </h2>
                    <p className="text-xs text-textSecondary mb-sm">
                      Från F-01
                      {data.f01.templateVersion !== null
                        ? ` v${data.f01.templateVersion}`
                        : ""}{" "}
                      (
                      {data.f01.source === "lead"
                        ? "webbens kartläggning"
                        : "appen"}
                      ) — inlämnad {fmt(data.f01.submittedAt)} • samma datapunkt
                      som F-02:s ”{desiredOutcomeField().label.sv}”
                    </p>
                    <ul className="flex flex-col gap-xs text-sm">
                      {data.f01.desiredOutcome.map((v) => (
                        <li key={v}>{desiredOutcomeLabel(v)}</li>
                      ))}
                      {data.f01.desiredOutcomeOther ? (
                        <li className="text-textSecondary">
                          Annat: {data.f01.desiredOutcomeOther}
                        </li>
                      ) : null}
                    </ul>
                    {/* TES-524 §2 — the second data point F-02 reuses from
                        F-01, shown with the same source/version/timestamp. */}
                    {data.f01.reproductiveStatus ? (
                      <p className="text-sm mt-sm">
                        <span className="text-textSecondary">
                          Reproduktiv status (F-01, samma inlämning):{" "}
                        </span>
                        {reproductiveStatusLabel(data.f01.reproductiveStatus)}
                      </p>
                    ) : null}
                  </section>
                ) : null}

                {/* TES-564 — the BMI 27–30 assessment block: her F-02
                    comorbidity answers with the doctor's verification. Sits
                    with the clinical material, above the full declaration. */}
                <ComorbidityAssessmentPanel patientId={data.patient.id} />

                {/* TES-1116 — BMI 25,0–26,9: WHtR and Kirill's possible
                    indicators from F-02's low-BMI section. Renders nothing
                    for a patient without the section. */}
                <LowBmiAssessmentPanel patientId={data.patient.id} />

                {/* CAR-118 — structured pre-consult summary: answers grouped by
              section with values resolved (no raw JSON needed to read it).

              TES-506 — collapsed. The 2026-09-04 walkthrough: the whole health
              declaration does not need to be expanded, but a summary does. The
              card above IS that summary; this is the full submission behind it,
              one click away. The flags stay expanded above, in "Riskflaggor: X"
              — the part a doctor must not have to open anything to see. */}
                {data?.preConsultSummary &&
                data.preConsultSummary.sections.length > 0 ? (
                  <details className="bg-surface border border-surfaceMuted rounded-md p-md">
                    <summary className="cursor-pointer font-display text-lg">
                      Hälsodeklaration ({data.preConsultSummary.answeredCount}{" "}
                      svar)
                    </summary>
                    <div className="mt-sm">
                      <SummarySections
                        sections={data.preConsultSummary.sections}
                      />
                    </div>
                  </details>
                ) : null}

                {/* Raw answers. Collapsed: the labelled summary directly above is
                the same submission rendered for a human, and the spec asks for
                "endast relevanta källsvar". A JSON blob of `med_*` keys as the
                default eyeful buries that summary — but it stays one click
                away, because it is the only view that shows an answer the
                curated summary leaves out. */}
                <details className="bg-surface border border-surfaceMuted rounded-md p-md">
                  <summary className="cursor-pointer font-display text-lg">
                    Alla svar (rådata)
                  </summary>
                  <p className="text-xs text-textSecondary mt-sm mb-sm">
                    Hälsodeklaration v{intake.templateVersion} — inlämnad{" "}
                    {fmt(intake.submittedAt)} • språk{" "}
                    {intake.submittedInLocale.toUpperCase()}
                  </p>
                  <pre className="text-xs bg-surfaceMuted/40 p-sm rounded-sm overflow-x-auto">
                    {JSON.stringify(intake.answers, null, 2)}
                  </pre>
                </details>
              </>
            ) : (
              <p className="text-textSecondary">
                Ingen hälsodeklaration inlämnad än.
              </p>
            )}

            {/* TES-863 (Ö-211) — every measured occasion, with the doctor's
                correction beside the original. Renders nothing for a role
                the route refuses. */}
            {id ? (
              <PatientMeasurements patientId={id} onUnauthorized={signOut} />
            ) : null}

            {/* TES-863 (Ö-211, groups 3/5/6) — medications, allergies and
                reproductive status, corrected beside the declaration. Doctor
                only; renders nothing for a role the route refuses. */}
            {id ? (
              <PatientMedicalProfile patientId={id} onUnauthorized={signOut} />
            ) : null}

            {/* Cycle history — part of the overview tab. */}
            {data.treatment && data.treatment.cycles.length > 0 ? (
              <section
                id="cycles"
                className="bg-surface border border-surfaceMuted rounded-md p-md"
              >
                <h2 className="font-display text-lg mb-sm">Recept-cykler</h2>
                {
                  <ul className="flex flex-col gap-xs text-sm">
                    {data.treatment.cycles.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between border-b border-surfaceMuted last:border-b-0 py-xs"
                      >
                        <span>
                          Cykel {c.cycleNumber} —{" "}
                          {cycleStateLabel(c.state, {
                            awaitingSubscription:
                              data.clinicalStage ===
                              "approved_awaiting_subscription",
                          })}
                          {c.dose ? ` • ${c.dose}` : ""}
                          {/* TES-997 — the prescription choice the publish
                              recorded; the stamp itself, not the choice,
                              says "issued". */}
                          {c.prescribedAt
                            ? ` • ${PRESCRIPTION_CHOICE_LABEL.new_issued}`
                            : isPrescriptionChoice(c.prescriptionChoice)
                              ? ` • ${PRESCRIPTION_CHOICE_LABEL[c.prescriptionChoice]}${
                                  c.existingPrescriptionValidUntil
                                    ? ` (gäller till ${c.existingPrescriptionValidUntil})`
                                    : ""
                                }`
                              : ""}
                        </span>
                        <span className="flex items-center gap-sm text-textSecondary text-xs">
                          {!readOnly &&
                          ISSUABLE_CYCLE_STATES.includes(c.state) &&
                          !c.prescribedAt &&
                          c.prescriptionChoice !== undefined ? (
                            <button
                              type="button"
                              disabled={issueBusyId !== null}
                              onClick={() => {
                                setIssueError(null);
                                setIssueTarget({
                                  cycleId: c.id,
                                  cycleNumber: c.cycleNumber,
                                });
                              }}
                              className="rounded-md border border-brand/50 px-sm py-[2px] text-xs text-brand hover:bg-brand/10 disabled:opacity-50"
                            >
                              {issueBusyId === c.id
                                ? "Registrerar…"
                                : PRESCRIPTION_CHOICE_LABEL.new_issued}
                            </button>
                          ) : null}
                          {fmt(c.stateChangedAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                }
              </section>
            ) : null}

            {/* TES-506 — the content surfaces, collapsed.
          The 2026-09-04 walkthrough: the patient view holds a lot of
          information the doctor does not need. Four of the panels on this tab —
          the home screen preview, the flow assignment, the journeys and the
          wellness programme — are our configuration of the product, the same
          class of surface that TES-492 moved out of the doctor's menu but left
          here. They are not removed: the doctor has grants for them and
          sometimes needs them. They are one click away, below the clinical
          sections.
          The key is PREFIXED per panel: children of the same parent collide on
          a bare `id`, and React then renders each panel twice (the first copy
          frozen mid-load) and does not unmount them when the tab changes. */}
            {id ? (
              <details className="bg-surface border border-surfaceMuted rounded-md p-md">
                <summary className="cursor-pointer font-display text-lg">
                  Innehåll och flöden
                </summary>
                <div className="mt-md flex flex-col gap-md">
                  <PatientHomeNow
                    key={`home-now:${id}`}
                    patientId={id}
                    locale={data.patient.preferredLocale === "en" ? "en" : "sv"}
                  />
                  <PatientFlows key={`flows:${id}`} patientId={id} />
                  <PatientJourneys key={`journeys:${id}`} patientId={id} />
                  <PatientWellnessProgram
                    key={`wellness:${id}`}
                    patientId={id}
                  />
                  <PatientNutritionCourses
                    key={`nutrition-courses:${id}`}
                    patientId={id}
                  />
                </div>
              </details>
            ) : null}
          </div>
          <aside className="flex flex-col gap-lg">
            {/* TES-1168 — "Boka nytt vårdmöte". The doctor books; TES-1195:
                so does the admin in her support view of the doctor's page
                (TES-1219: the component decides who books). */}
            {id ? (
              <div id={MEDICAL_ACTION_ANCHOR.propose_meeting}>
                <BookCareMeeting
                  key={`book-meeting:${id}`}
                  patientId={id}
                  onBooked={() => {
                    load();
                    void actionCreated("propose_meeting");
                  }}
                  onUnauthorized={() => void signOut()}
                />
              </div>
            ) : null}
            {upcomingMeetings.length > 0 ? (
              <section className="rounded-md border border-surfaceMuted bg-surface p-md">
                <h2 className="mb-sm font-display text-lg">Kommande möten</h2>
                <ul className="flex flex-col gap-sm text-sm">
                  {upcomingMeetings.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-baseline justify-between gap-sm"
                    >
                      <span>{fmt(m.scheduledAt)}</span>
                      {/* TES-811 (Ö-184) — the row used to link to the detail
                          view whatever it was booked on, so a doctor clicked
                          through to a guaranteed 403 that read
                          "Du saknar behörighet" and looked like a permission
                          bug. It is not: the consult simply belongs to a
                          colleague. Say whose it is instead of offering a door
                          that is locked. `canOpen === undefined` is an older
                          API and keeps the previous behaviour. */}
                      {m.canOpen === false ? (
                        <span
                          className="shrink-0 text-xs text-textSecondary"
                          title="Vårdmötet är bokat på en annan läkare"
                        >
                          {m.doctorDisplayName ?? "Annan läkare"}
                        </span>
                      ) : (
                        <Link
                          to={viewPath(patientMeetingHref(id, m.id))}
                          className="shrink-0 text-brand hover:underline"
                        >
                          Öppna
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {/* TES-292 [J20] — coach↔doctor handoff / escalation from the patient page. */}
            {/* TES-519 — open handoffs with their next action, then the create form. */}
            {/* TES-869 (Ö-209/Ö-210) — the care-case correction. TES-862
                shipped the endpoint in both directions and nothing called it;
                this is the surface. Doctor-only by the route's own @Roles, so
                a coach simply never sees a panel he could not submit. */}
            {id && data.patientGroup ? (
              <CareCaseCorrection
                patientId={id}
                patientGroup={data.patientGroup}
                onCorrected={load}
                onUnauthorized={() => void signOut()}
              />
            ) : null}
            {id ? <PatientOpenHandoffs patientId={id} /> : null}
            {id && !readOnly ? (
              <details className="rounded-md border border-surfaceMuted bg-surface p-md">
                <summary className="cursor-pointer font-display text-lg">
                  Skapa överlämning
                </summary>
                <div className="mt-sm">
                  <PatientHandoff patientId={id} />
                </div>
              </details>
            ) : null}

            {/* TES-381 — body photos from milestone check-ins / freeform uploads.
          Metadata list + click-to-view via authenticated blob fetch. */}
            {/* TES-1040 — body photos are not opened to an admin (the API
                answers 403), so the section is left out, not shown failing. */}
            {id && !readOnly ? (
              <PatientPhotos
                key={`photos:${id}`}
                patientId={id}
                onUnauthorized={() => void signOut()}
              />
            ) : null}
          </aside>
        </div>
      ) : null}

      {/* CAR-92: weekly check-in history */}
      {tab === "test" && id ? <TestPatientPanel patientId={id} /> : null}

      {/* TES-519 — the Incheckningar lane lands here. Flagged, unreviewed
          check-ins first; "Markera som granskad" stamps reviewedAt, which is
          what takes the row out of the lane and the count off the badge. */}
      {/* TES-986 — the medical review cases sit above the check-ins: a RED
          one is the first thing on this tab, and the queue links here. */}
      {tab === "veckor" && id ? (
        <MedicalReviewCases
          patientId={id}
          onUnauthorized={() => void signOut()}
        />
      ) : null}

      {tab === "veckor" &&
        (weeklyCheckins.length > 0 ? (
          <section className="bg-surface border border-surfaceMuted rounded-md p-md">
            <h2 className="font-display text-lg mb-sm">Veckans check-in</h2>
            <p className="text-xs text-textSecondary mb-md">
              {weeklyCheckins.length} inlämnade check-in. Ogranskade med flaggor
              först, sedan senaste först.
            </p>
            <ul className="flex flex-col gap-sm">
              {[...weeklyCheckins]
                .sort((a, b) => {
                  const ap =
                    !a.reviewedAt && a.flags.length + a.hardStops.length > 0
                      ? 0
                      : 1;
                  const bp =
                    !b.reviewedAt && b.flags.length + b.hardStops.length > 0
                      ? 0
                      : 1;
                  return ap - bp || b.submittedAt.localeCompare(a.submittedAt);
                })
                .map((c) => {
                  const nFlags = c.flags.length + c.hardStops.length;
                  const needsReview = !c.reviewedAt && nFlags > 0;
                  return (
                    <li
                      key={c.id}
                      className={`rounded-sm border p-sm ${needsReview ? "border-warning/60 bg-warning/5" : "border-surfaceMuted"}`}
                    >
                      <div className="flex flex-wrap items-center gap-sm mb-xs">
                        <p className="text-xs text-textSecondary">
                          v{c.templateVersion} — inlämnad {fmt(c.submittedAt)} •
                          språk {c.submittedInLocale.toUpperCase()}
                        </p>
                        {c.reviewedAt ? (
                          <span className="text-xs text-success">
                            Granskad {fmt(c.reviewedAt)}
                          </span>
                        ) : nFlags > 0 && !readOnly ? (
                          <button
                            type="button"
                            disabled={reviewBusyId === c.id}
                            onClick={() => void reviewSubmission(c.id)}
                            className="ml-auto rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50"
                          >
                            {reviewBusyId === c.id
                              ? "Sparar…"
                              : "Markera som granskad"}
                          </button>
                        ) : null}
                      </div>
                      {nFlags > 0 ? (
                        <div className="flex flex-wrap gap-xs mb-xs">
                          {c.hardStops.map((h) => (
                            <span
                              key={`hs-${h.rule}`}
                              className="rounded-pill border border-danger/40 bg-danger/10 px-sm py-[2px] text-xs text-danger"
                              title={h.rule}
                            >
                              {flagRuleLabel(h.rule)}
                            </span>
                          ))}
                          {c.flags.map((f) => (
                            <span
                              key={`f-${f.rule}`}
                              className="rounded-pill border border-warning/40 bg-warning/10 px-sm py-[2px] text-xs text-warning"
                              title={f.rule}
                            >
                              {flagRuleLabel(f.rule)}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <WeeklyCheckinAnswers answers={c.answers} />
                    </li>
                  );
                })}
            </ul>
          </section>
        ) : (
          <p className="text-textSecondary">Inga veckoincheckningar än.</p>
        ))}

      {/* CAR-117 + TES-201 — lab orders + results. For a resulted order the
          doctor reviews each analyte and curates patient-facing feedback; the
          green "Godkänn och skicka till patient" both shares the feedback and
          approves the order (which unlocks the activate button). */}
      {tab === "blodprov" && (
        <MedicalEvidencePanel
          patientId={id}
          onUnauthorized={() => void signOut()}
          reloadKey={evidenceKey}
          onLoaded={(d) => {
            setBloodTest(d.bloodTest ?? null);
            setEvidence(d);
          }}
        />
      )}
      {/* TES-942 (TES-941 §STATUSKONSISTENS) — the current case's blood test
          reads the server's ONE derived status, the same one the patient's
          checklist is built from. Only the order that belongs to the case is
          shown as current; every other order is history under "Tidigare",
          so an order from before the case can never read as this case's
          result. (TES-952: the doctor acts through the decision above.) */}
      {/* TES-952 (TES-941 §WEBDOC OCH BEHANDLINGSBESLUT) — the one
          decision, first on the Blodprov tab: there is no Webdoc
          integration, so Kirill reads the result there and comes here to
          answer her and decide. A transfer patient is decided in her
          review, which has its own decision and completion picker. */}
      {tab === "blodprov" && (
        <section
          id={MEDICAL_ACTION_ANCHOR.treatment_plan}
          className="bg-surface border border-surfaceMuted rounded-md p-md"
          data-testid="treatment-decision-card"
        >
          <h2 className="font-display text-lg mb-sm">Behandlingsbeslut</h2>
          {canDecide ? (
            <div className="flex flex-col gap-xs">
              <button
                type="button"
                onClick={() => setDecisionOpen(true)}
                className="w-fit rounded-md bg-brand px-md py-sm text-sm text-textOnBrand hover:opacity-90"
              >
                Lämna svar till patienten och fatta behandlingsbeslut
              </button>
              <p className="text-xs text-textSecondary">
                {labOrderToAnswer(bloodTest)
                  ? "Läs provsvaret i Webdoc. Provsvaret registreras som inkommet och granskat när du publicerar beslutet."
                  : "Svar, behandlingsplan, beslut och abonnemangslänk i ett steg."}
              </p>
            </div>
          ) : data.patientGroup === "PRESCRIPTION_TRANSFER" &&
            data.transferReview ? (
            <p className="text-sm text-textSecondary">
              Receptövertag beslutas under fliken{" "}
              <button
                type="button"
                onClick={() => setTab("overtag")}
                className="text-brand underline"
              >
                Receptövertag
              </button>
              .
            </p>
          ) : (
            <p className="text-sm text-textSecondary">
              {positiveDecisionPublished
                ? "Positivt behandlingsbeslut är publicerat."
                : "Inget beslut att fatta i det aktuella ärendet."}
            </p>
          )}
          {positiveDecisionPublished &&
          !readOnly &&
          offersSubscriptionLinkResend(data.subscription?.standing) ? (
            <div className="mt-sm flex flex-col gap-xs">
              <p className="text-xs font-semibold text-textSecondary">
                Skicka abonnemangslänken igen (länken gäller i 72 timmar)
              </p>
              <SubscriptionPlanChoice
                patientId={id}
                busy={subLinkBusy}
                onSend={(choice) => void resendLink(choice)}
                initial={resendPlanPrefill(data.transferReview?.planChoice)}
              />
            </div>
          ) : null}
        </section>
      )}
      {tab === "blodprov" && (
        <section className="bg-surface border border-surfaceMuted rounded-md p-md">
          <h2 className="font-display text-lg mb-sm">Blodprov</h2>
          {bloodTest === undefined ? null : bloodTest === null ? (
            <p className="text-textSecondary text-sm mb-md">
              Inget nytt blodprov begärt i det aktuella ärendet.
            </p>
          ) : (
            <div className="mb-md" data-testid="blood-test-current">
              <p className="text-sm">
                Aktuellt ärende:{" "}
                <span className="font-semibold">
                  {BLOOD_TEST_STATUS_LABELS[bloodTest.status]}
                </span>
                {bloodTest.status === "reviewed" && bloodTest.reviewOutcome
                  ? ` — ${REVIEW_OUTCOME_LABEL[bloodTest.reviewOutcome as keyof typeof REVIEW_OUTCOME_LABEL] ?? bloodTest.reviewOutcome}`
                  : ""}
              </p>
              <p className="text-xs text-textSecondary">
                {!bloodTest.paymentRequired
                  ? "Ingen betalning i det här ärendet"
                  : bloodTest.paidAt
                    ? `Betalt ${fmt(bloodTest.paidAt)}`
                    : "Ej betalt"}
                {bloodTest.sampleReportedAt
                  ? ` • patienten markerade provet som lämnat ${fmt(bloodTest.sampleReportedAt)}`
                  : ""}
                {bloodTest.resultsReceivedAt
                  ? ` • provsvar markerat som inkommet ${fmt(bloodTest.resultsReceivedAt)}`
                  : ""}
              </p>
              {/* TES-1179 — the case's order has a LabPortalen answer that
                  could not be read in, so "inväntar provsvar" above is not
                  the whole truth. The details are on the order below. */}
              {bloodTest.status !== "result_registered" &&
              bloodTest.status !== "reviewed" &&
              data.labOrders.some(
                (o) =>
                  o.id === bloodTest.orderId && o.quarantinedReports?.length,
              ) ? (
                <p
                  className="mt-xs text-xs text-warning"
                  data-testid="blood-test-unreadable-report"
                >
                  {UNREADABLE_LAB_REPORT_LABEL} – se beställningen nedan.
                </p>
              ) : null}
              {/* TES-1060 (TES-1054 point E) — the explicit action on the
                  current lab case, from the paid sample until the result is
                  reviewed. */}
              {!readOnly &&
              bloodTest.orderId &&
              bloodTest.status !== "awaiting_payment" &&
              bloodTest.status !== "reviewed" ? (
                <div className="mt-sm flex flex-col gap-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setLabWebdocNotice(null);
                      setLabWebdocOpen(true);
                    }}
                    className="w-fit rounded-md border border-brand px-md py-sm text-sm text-brand hover:bg-brand/5"
                  >
                    {LAB_WEBDOC_ACTION_LABEL}
                  </button>
                </div>
              ) : null}
              {labWebdocNotice ? (
                <p className="mt-xs text-xs text-textSecondary">
                  {labWebdocNotice}
                </p>
              ) : null}
            </div>
          )}
          {(() => {
            const renderOrder = (order: PatientDetail["labOrders"][number]) => (
              <li
                key={order.id}
                className="border border-surfaceMuted rounded-sm p-sm"
              >
                <div className="flex items-center justify-between gap-md">
                  <p className="text-xs text-textSecondary">
                    Status:{" "}
                    <span className="text-textPrimary">
                      {LAB_ORDER_STATUS_LABEL[order.status] ?? order.status}
                    </span>{" "}
                    • beställd {fmt(order.orderedAt)}
                  </p>
                  {order.reviewedAt ? (
                    <span
                      className={
                        order.reviewOutcome === "approved" ||
                        !order.reviewOutcome
                          ? "text-xs text-success"
                          : "text-xs text-warning"
                      }
                    >
                      {REVIEW_OUTCOME_LABEL[order.reviewOutcome ?? "approved"]}{" "}
                      {fmt(order.reviewedAt)}
                    </span>
                  ) : order.status === "resulted" ? (
                    <span className="text-xs text-warning">
                      Inväntar granskning
                    </span>
                  ) : order.quarantinedReports?.length ? (
                    // TES-1179 — an answer arrived; it is not waiting.
                    <span className="text-xs text-warning">
                      {UNREADABLE_LAB_REPORT_LABEL}
                    </span>
                  ) : order.status === "partial" ? (
                    // TES-1337 — some values arrived; the rest are awaited.
                    // Proposed copy.
                    <span className="text-xs text-textSecondary">
                      Inväntar resterande värden
                    </span>
                  ) : (
                    <span className="text-xs text-textSecondary">
                      Inväntar svar
                    </span>
                  )}
                </div>
                <LabOrderQuarantinedReports
                  reports={order.quarantinedReports}
                />
                {/* TES-1337 — what the lab delivered: its comments on the
                    order and on each value, and on a partial delivery which
                    values are still missing. */}
                <LabOrderResults order={order} />
                {/* TES-442 — the remiss number LabPortalen answers are
                    matched on, editable; and the id learned from them. */}
                {id ? (
                  <LabOrderRequisitionRef
                    patientId={id}
                    orderId={order.id}
                    requisitionRef={order.requisitionRef ?? null}
                    labPortalenRid={order.labPortalenRid ?? null}
                    labPortalenExternalRid={
                      order.labPortalenExternalRid ?? null
                    }
                    locked={order.status === "cancelled"}
                    onUnauthorized={() => void signOut()}
                  />
                ) : null}
                {/* TES-952 — no per-order buttons any more. "Provsvar har
                    kommit in" (TES-506) and "Provsvar granskat" (TES-890)
                    are written by the treatment decision above when it is
                    published (Ö-242's assumption (a)). */}
                {/* TES-1370 (Ö-582) — the review-and-publish panel, for a
                    partial result too (a Delsvar), wherever the server says
                    the box publishes lab results to the patient. */}
                {id && order.patientFeedbackAvailable ? (
                  <LabFeedbackPanel
                    patientId={id}
                    orderId={order.id}
                    onSent={reloadLabs}
                    onUnauthorized={() => void signOut()}
                  />
                ) : null}
              </li>
            );
            const current = data.labOrders.filter(
              (o) => bloodTest && o.id === bloodTest.orderId,
            );
            const earlier = data.labOrders.filter(
              (o) => !(bloodTest && o.id === bloodTest.orderId),
            );
            return (
              <>
                {current.length > 0 ? (
                  <ul className="flex flex-col gap-md mb-md">
                    {current.map(renderOrder)}
                  </ul>
                ) : null}
                {earlier.length > 0 ? (
                  <>
                    <h3 className="text-xs font-semibold text-textSecondary mb-xs">
                      Tidigare
                    </h3>
                    <ul className="flex flex-col gap-md">
                      {earlier.map(renderOrder)}
                    </ul>
                  </>
                ) : null}
                {data.labOrders.length === 0 ? (
                  <p className="text-textSecondary text-sm">
                    Inga labbprover registrerade i Ella.
                  </p>
                ) : null}
              </>
            );
          })()}
        </section>
      )}

      {/* TES-1060 (TES-1054 point C) — the Kompletteringsanteckning of the
          newest round with something supplied, beside (never merged into)
          the earlier note. */}
      {tab === "blodprov" && id && !readOnly && noteRoundId ? (
        <CompletionNoteWorkspace
          key={noteRoundId}
          patientId={id}
          roundId={noteRoundId}
          reloadKey={evidenceKey}
          onUnauthorized={() => void signOut()}
        />
      ) : null}

      {/* TES-519 — the monthly check-in: the patient's answers + the decision,
          on the patient screen. The rows in the "Månadsincheckningar" and
          "Biverkningar" lanes land here. */}
      {tab === "manad" && id && data.renewalReview ? (
        <section className="rounded-md border border-surfaceMuted bg-surface p-md">
          <h2 className="mb-xs font-display text-lg">
            Månadsincheckning — cykel {data.renewalReview.cycleNumber}
          </h2>
          <p className="mb-sm text-xs text-textSecondary">
            {data.renewalReview.state === "doctor_review"
              ? "Under granskning"
              : "Inväntar granskning"}
            {data.renewalReview.dose
              ? ` · nuvarande dos ${data.renewalReview.dose}`
              : ""}
            {data.renewalReview.expiresAt
              ? ` · cykeln går ut ${data.renewalReview.expiresAt.slice(0, 10)}`
              : ""}
          </p>
          {/* TES-998 — a keep-wish belongs to this review. */}
          <GoalZoneNote
            summary={data.goalZone}
            className="mt-sm rounded border border-brand/30 bg-brand/5 px-sm py-xs text-sm text-textPrimary"
          />
          <RenewalReviewPanel
            patientId={id}
            cycleId={data.renewalReview.cycleId}
            renewal={data.renewalReview.answers}
            goalZone={data.goalZone}
            treatmentStartState={data.treatmentStartState}
            monthlyCheckinMissing={data.renewalReview.monthlyCheckinMissing}
            monthlyDiscussion={data.renewalReview.monthlyDiscussion}
            followUp={data.renewalReview.followUp}
            onDone={() => {
              load();
              void actionCreated("prescription_review");
            }}
            onError={(e) => {
              if (e instanceof UnauthorizedError) {
                void signOut();
                return;
              }
              setError(errorText(e instanceof Error ? e.message : String(e)));
            }}
            currentOrder={data.renewalReview}
            onUnauthorized={() => void signOut()}
          />
        </section>
      ) : null}

      {/* TES-506 — the meetings and their documentation. See PatientMeetings.
          TES-955 — with a meeting open, the tab IS the meeting. The workspace
          stays mounted (hidden) on the other tabs while `?mote=` is set, so a
          call and an unsaved note survive a look at the overview. */}
      {id && meetingId ? (
        <MeetingWorkspace
          key={meetingId}
          patientId={id}
          meetingId={meetingId}
          patient={data}
          visible={tab === "moten"}
          onBackToList={() => setMeeting(null)}
          onDocumented={load}
          onCallChange={setInCall}
          alarmActions={{
            busyAlarmId: flagBusyId,
            onAcknowledge: (alarmId) => void acknowledgeFlag(alarmId),
            onClear: (alarmId) => void clearFlag(alarmId),
          }}
        />
      ) : null}
      {tab === "moten" && id && !meetingId ? (
        // `?? []` for the same reason as `.optional()` on the other late
        // fields: the client does not validate the response with zod but casts
        // it, so an admin running against an older API would have crashed on a
        // missing list instead of showing an empty tab.
        <PatientMeetings patientId={id} meetings={data.meetings ?? []} />
      ) : null}

      {/* Patient chat (CAR-17). TES-506: `?kanal=` carries which conversation
          was clicked in the chat list, so the pane opens on that channel
          instead of always on the viewer's own. */}
      {tab === "chat" && !readOnly && (
        <PatientChatPane
          patientId={id}
          onUnauthorized={() => void signOut()}
          initialChannel={
            searchParams.get("kanal") === "coach"
              ? "coach"
              : searchParams.get("kanal") === "doctor"
                ? "doctor"
                : undefined
          }
        />
      )}

      {/* TES-506 — the coach-message overrides used to render here too.
          The 2026-09-04 walkthrough: doctors should not see suggested coach
          messages. They are the coach's material, and offering a
          doctor a panel for editing them invites her to answer as the coach in
          a channel that is not hers. The panel is unchanged and still lives on
          the coach's own patient page (CoachPatientDetail). */}

      {issueTarget && !readOnly ? (
        <IssuePrescriptionDialog
          key={issueTarget.cycleId}
          cycleNumber={issueTarget.cycleNumber}
          busy={issueBusyId !== null}
          error={issueError}
          onConfirm={() => void recordIssuedPrescription(issueTarget.cycleId)}
          onClose={() => setIssueTarget(null)}
        />
      ) : null}
      {labWebdocOpen && id && bloodTest?.orderId && !readOnly ? (
        <LabWebdocAssessmentDialog
          key={bloodTest.orderId}
          patientId={id}
          orderId={bloodTest.orderId}
          roundId={evidence ? (currentRound(evidence)?.id ?? null) : null}
          onDone={(result) => {
            setLabWebdocOpen(false);
            setLabWebdocNotice(labWebdocDoneNotice(result));
            reloadLabs();
          }}
          onClose={() => setLabWebdocOpen(false)}
          onUnauthorized={() => void signOut()}
        />
      ) : null}
      {decisionOpen && canOpenDecision ? (
        <TreatmentDecisionWorkspace
          // Keyed on the patient: a draft decision must never carry over.
          key={id}
          patientId={id}
          data={data}
          bloodTest={bloodTest}
          onClose={() => setDecisionOpen(false)}
          onPublished={onDecisionPublished}
          onStatusPublished={onStatusDecisionPublished}
          onUnauthorized={() => void signOut()}
        />
      ) : null}
    </div>
  );
}
